import { type CancelToken } from 'axios';
import {
  useCallback, useEffect, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import { isCancelError } from '~/_shared/utils/api/apiError.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import {
  type ValidateMOTConstraintsItem,
  validateOptimizedTerritoryConstraints,
} from '../../../createOptimizedTerritoriesModal/optimizedTerritories.repository';
import { type MetricValue } from '../../createBoundaryGroupFromAIModal.types';
import { getConstraintValidationErrorMessages } from './constraints.helpers';

type OptimizedTerritoriesConstraintsValidationParams = {
  optimizationConstraints: boolean;
  dataMetricsValues: MetricValue[];
  demographicsValues: MetricValue[];
  boundaryGroupId: number | null;
};

export const useOptimizedTerritoriesConstraintsValidation = ({
  boundaryGroupId,
  optimizationConstraints,
  demographicsValues,
  dataMetricsValues,
}: OptimizedTerritoriesConstraintsValidationParams) => {
  const [validationData, setValidationData] = useState<ConstraintsValidationDataMap>(new Map());
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();

  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const validateConstraints = useCallback(async (cancelToken: CancelToken) => {
    if (!clientId || mapId === null || spreadsheetId === null || boundaryGroupId === null) {
      return;
    }

    let isAnyConstraintLimited = false;
    const constraints: ValidateMOTConstraintsItem[] = [
      ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.isConstraint && dataMetricsValue.checked).map(dataMetricsValue => {
        if (dataMetricsValue.constraintLowerBound !== undefined || dataMetricsValue.constraintUpperBound !== undefined) {
          isAnyConstraintLimited = true;
        }
        return {
          attribute: dataMetricsValue.id,
          lower_bound: dataMetricsValue.constraintLowerBound ?? 1,
          upper_bound: dataMetricsValue.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
        };
      }),
      ...demographicsValues.filter(demographicsValue => demographicsValue.isConstraint && demographicsValue.checked).map(demographicsValue => {
        if (demographicsValue.constraintLowerBound !== undefined || demographicsValue.constraintUpperBound !== undefined) {
          isAnyConstraintLimited = true;
        }
        return {
          attribute: demographicsValue.id,
          lower_bound: demographicsValue.constraintLowerBound ?? 1,
          upper_bound: demographicsValue.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
        };
      }),
    ];

    if (constraints.length === 0 || !isAnyConstraintLimited) {
      return;
    }

    setIsLoading(true);

    try {
      const validationResult = await validateOptimizedTerritoryConstraints(clientId, {
        map_id: mapId,
        constraints,
        boundary_group_id: boundaryGroupId,
        spreadsheet_id: spreadsheetId,
      }, cancelToken);

      setGeneralError('');

      if (validationResult && validationResult.results.length) {
        const newValidationData: ConstraintsValidationDataMap = new Map();

        for (const validationItem of validationResult.results) {
          const errorMessages = getConstraintValidationErrorMessages(validationItem, t);

          newValidationData.set(validationItem.attribute, {
            errorMessages,
            highestMin: validationItem.limits.highestMin,
            lowestMax: validationItem.limits.lowestMax,
            max: validationItem.limits.max,
            min: validationItem.limits.min,
          });
        }

        setValidationData(newValidationData);
      }
    }
    catch (err) {
      if (!isCancelError(err)) {
        setValidationData(new Map());
        setGeneralError(t('Something went wrong, please try again.'));
      }
    }
    finally {
      setIsLoading(false);
    }
  }, [clientId, mapId, spreadsheetId, boundaryGroupId, dataMetricsValues, demographicsValues, t]);

  useEffect(() => {
    if (!optimizationConstraints) {
      return;
    }

    const cancelToken = createCancelToken();

    const timeoutId = setTimeout(() => {
      validateConstraints(cancelToken.token);
    }, 750);

    return () => {
      clearTimeout(timeoutId);
      cancelToken.cancel();
    };
  }, [optimizationConstraints, validateConstraints]);

  return {
    areConstraintsValid: !isLoading && (optimizationConstraints
      ? Array.from(validationData.values()).every(validation => validation.errorMessages.length === 0)
      : true),
    generalError,
    isLoading,
    validationData,
  };
};
